import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Phone, Mail, Clock, Car, Train } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { dojoData } from '../mock';
import ContactForm from './ContactForm';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Component to update map view when location changes
const MapUpdater = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    map.setView(center, zoom);
  }, [map, center, zoom]);
  
  return null;
};

const Location = () => {
  const { locations } = dojoData;
  const [selectedLocation, setSelectedLocation] = useState(locations[0]);

  useEffect(() => {
    // Check hash for location selection
    const handleLocationFromHash = () => {
      const hash = window.location.hash;
      
      if (hash.includes('?loc=')) {
        const locationId = hash.split('?loc=')[1];
        const location = locations.find(loc => loc.id === locationId);
        
        if (location) {
          setSelectedLocation(location);
          
          // Ensure we scroll to the location section
          setTimeout(() => {
            const locationSection = document.getElementById('location');
            if (locationSection) {
              const headerHeight = 80;
              const elementPosition = locationSection.offsetTop - headerHeight;
              window.scrollTo({
                top: elementPosition,
                behavior: 'smooth'
              });
            }
          }, 100);
        }
        // Clean up URL hash after using it
        window.history.replaceState({}, '', '#location');
      }
    };

    // Handle hash changes (when clicking location links)
    const handleHashChange = () => {
      handleLocationFromHash();
    };

    // Handle initial load
    handleLocationFromHash();

    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [locations]);

  return (
    <section id="location" className="section-padding section-light">
      <div className="container">
        {/* Section Header */}
        <div className="text-center mb-10">
          <h2 className="heading-1 mb-4" style={{ hyphens: 'none', wordBreak: 'normal', overflowWrap: 'break-word' }}>Nous rendre visite</h2>
          <p className="body-large max-w-2xl mx-auto" style={{ color: 'var(--text-on-light)' }}>
            Localisé hors du centre-ville avec un accès facile et un grand parking.
          </p>
        </div>

        {/* Location Selector */}
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          {locations.map((location) => (
            <button
              key={location.id}
              onClick={() => setSelectedLocation(location)}
              className={`px-6 py-3 rounded-lg border transition-all ${
                selectedLocation.id === location.id 
                  ? 'btn-primary' 
                  : 'border-opacity-50 hover:border-opacity-100'
              }`}
              style={{
                ...(selectedLocation.id !== location.id && {
                  backgroundColor: 'transparent',
                  color: 'var(--text-secondary-light)',
                  borderColor: 'var(--border-medium)'
                })
              }}
            >
              {location.name}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Interactive Map */}
          <div className="order-2 lg:order-1">
            <div 
              className="w-full h-80 rounded-lg overflow-hidden border" 
              style={{ borderColor: 'var(--border-medium)' }}
            >
              <style>
                {`
                  .leaflet-container {
                    z-index: 1 !important;
                  }
                  .leaflet-control-zoom {
                    z-index: 2 !important;
                  }
                  .leaflet-control-attribution {
                    z-index: 2 !important;
                  }
                  .leaflet-popup-pane {
                    z-index: 3 !important;
                  }
                  .leaflet-control-zoom a {
                    background-color: var(--bg-light) !important;
                    color: var(--brand-primary) !important;
                    border-color: var(--border-medium) !important;
                  }
                  .leaflet-control-zoom a:hover {
                    background-color: var(--brand-primary) !important;
                    color: white !important;
                  }
                  .leaflet-control-attribution {
                    background-color: rgba(255, 255, 255, 0.9) !important;
                    font-size: 10px !important;
                  }
                  .leaflet-popup-content-wrapper {
                    border-radius: 8px !important;
                  }
                `}
              </style>
              <MapContainer
                center={[locations[0].coordinates.lat, locations[0].coordinates.lng]}
                zoom={15}
                style={{ height: '100%', width: '100%' }}
                scrollWheelZoom={true}
                zoomControl={true}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                
                <MapUpdater 
                  center={[selectedLocation.coordinates.lat, selectedLocation.coordinates.lng]} 
                  zoom={15} 
                />
                
                {/* Marker for selected location only */}
                <Marker
                  position={[selectedLocation.coordinates.lat, selectedLocation.coordinates.lng]}
                >
                  <Popup>
                    <div className="text-center p-2">
                      <h3 className="font-semibold mb-2 text-base" style={{ hyphens: 'none', wordBreak: 'normal', overflowWrap: 'break-word' }}>{selectedLocation.name}</h3>
                      <p className="text-sm mb-2 text-gray-600">{selectedLocation.address}</p>
                      <div className="text-xs space-y-1 mb-2">
                        <p><strong>📞</strong> <a href={`tel:${selectedLocation.phone}`} className="text-blue-600 hover:underline">{selectedLocation.phone}</a></p>
                        <p><strong>📧</strong> <a href={`mailto:${selectedLocation.email}`} className="text-blue-600 hover:underline">{selectedLocation.email}</a></p>
                      </div>
                      <div className="mt-2 text-xs">
                        <p className="font-semibold mb-1">Horaires:</p>
                        {Object.entries(selectedLocation.hours).map(([day, time]) => (
                          <p key={day} className="text-gray-600">{day}: {time}</p>
                        ))}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              </MapContainer>
            </div>
          </div>

          {/* Contact Information */}
          <div className="order-1 lg:order-2 space-y-6">
            {/* Address & Directions */}
            <div className="flex items-start gap-4">
              <MapPin size={24} style={{ color: 'var(--brand-primary)' }} className="flex-shrink-0 mt-1" />
              <div className="w-full">
                <h3 className="heading-3 mb-2" style={{ hyphens: 'none', wordBreak: 'normal', overflowWrap: 'break-word' }}>Comment venir</h3>
                <p className="body-medium break-words mb-4" style={{ color: 'var(--text-on-light)' }}>{selectedLocation.address}</p>
                
                {/* Transportation Methods */}
                <div className="space-y-3">
                  {/* Car */}
                  <details className="group">
                    <summary className="cursor-pointer flex items-center gap-2 text-sm font-medium hover:text-opacity-80 transition-colors" style={{ color: 'var(--brand-primary)' }}>
                      <Car size={16} />
                      En voiture
                      <span className="ml-auto group-open:rotate-180 transition-transform">▼</span>
                    </summary>
                    <div className="mt-2 pl-6 text-xs space-y-2" style={{ color: 'var(--text-on-light)' }}>
                      {selectedLocation.id === 'main' ? (
                        <>
                          <p><strong>Depuis l'autoroute A20 :</strong> Sortie 57 (Cahors Nord) ou 58 (Cahors Sud), suivre direction Cahors centre puis Cabessut.</p>
                          <p><strong>Depuis le centre :</strong> Direction Cabessut via le Pont Cabessut, puis rue de l'île (5 minutes).</p>
                          <p><strong>Stationnement :</strong> Parking gratuit P+R Ludo-Rollès avec navette (vous dépose à 500m) ou rues résidentielles le soir.</p>
                        </>
                      ) : (
                        <>
                          <p><strong>Depuis l'autoroute A20 :</strong> Sortie 58 (Cahors Sud) via D653 vers rue de la guinguette, ou Sortie 57 puis direction Cabessut.</p>
                          <p><strong>Stationnement :</strong> 100 places gratuites dédiées aux utilisateurs du complexe, dont 10 places handicapés. Disponibilité excellente le soir.</p>
                        </>
                      )}
                    </div>
                  </details>

                  {/* Public Transport */}
                  <details className="group">
                    <summary className="cursor-pointer flex items-center gap-2 text-sm font-medium hover:text-opacity-80 transition-colors" style={{ color: 'var(--brand-primary)' }}>
                      <Train size={16} />
                      Transport en commun GRATUIT
                      <span className="ml-auto group-open:rotate-180 transition-transform">▼</span>
                    </summary>
                    <div className="mt-2 pl-6 text-xs space-y-2" style={{ color: 'var(--text-on-light)' }}>
                      {selectedLocation.id === 'main' ? (
                        <>
                          <p><strong>Bus Evidence :</strong> Navette 2 jusqu'à l'arrêt "Cabessut" (lun-ven, 7h20-19h35, toutes les 15 min) ou Ligne 4 (arrêts Regourd/Branly).</p>
                          <p><strong>Depuis la gare SNCF :</strong> Navette 2 de "Gare SNCF" à "Cabessut" (15-20 minutes total).</p>
                        </>
                      ) : (
                        <>
                          <p><strong>Bus Evidence :</strong> Réseau desservant le quartier Cabessut avec correspondances vers ce site.</p>
                          <p><strong>Depuis la gare :</strong> Bus vers centre-ville puis correspondance (25-30 min total).</p>
                        </>
                      )}
                      <p><strong>⚠️ Important :</strong> Service limité samedi soir, inexistant dimanche. Renseignements : 0806 80 10 60</p>
                    </div>
                  </details>

                  {/* Walking */}
                  <details className="group">
                    <summary className="cursor-pointer flex items-center gap-2 text-sm font-medium hover:text-opacity-80 transition-colors" style={{ color: 'var(--brand-primary)' }}>
                      <span className="text-base">🚶</span>
                      À pied
                      <span className="ml-auto group-open:rotate-180 transition-transform">▼</span>
                    </summary>
                    <div className="mt-2 pl-6 text-xs space-y-2" style={{ color: 'var(--text-on-light)' }}>
                      {selectedLocation.id === 'main' ? (
                        <p><strong>Depuis le centre-ville :</strong> 15 minutes depuis le Boulevard Gambetta puis Pont Cabessut et rue de l'île.</p>
                      ) : (
                        <p><strong>Depuis le centre-ville :</strong> 15 minutes à pied via Pont Louis-Philippe et rue de la guinguette. Trottoirs aménagés sur tout le parcours.</p>
                      )}
                    </div>
                  </details>

                  {/* Landmarks */}
                  <details className="group">
                    <summary className="cursor-pointer flex items-center gap-2 text-sm font-medium hover:text-opacity-80 transition-colors" style={{ color: 'var(--brand-primary)' }}>
                      <span className="text-base">🏛️</span>
                      Points de repère
                      <span className="ml-auto group-open:rotate-180 transition-transform">▼</span>
                    </summary>
                    <div className="mt-2 pl-6 text-xs space-y-1" style={{ color: 'var(--text-on-light)' }}>
                      {selectedLocation.id === 'main' ? (
                        <>
                          <p>• <strong>L'Archipel Centre Aquatique</strong> (300m à l'est) - référence principale</p>
                          <p>• <strong>Pont Cabessut</strong> (600m au nord)</p>
                          <p>• Signalisation claire "Gymnase Henri-Thamier"</p>
                        </>
                      ) : (
                        <>
                          <p>• <strong>Piscine Divonéo</strong> (même adresse) - repère principal immanquable</p>
                          <p>• <strong>Mont Saint-Cyr</strong> (15 minutes) pour vue panoramique</p>
                          <p>• Restaurant Le Cabessut et Boulangerie Cabessut dans le quartier</p>
                        </>
                      )}
                    </div>
                  </details>
                </div>

                {/* Accessibility info for Complexe Sportif */}
                {selectedLocation.id === 'secondary' && (
                  <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-card-light)' }}>
                    <p className="text-xs mb-1 font-medium" style={{ color: 'var(--brand-primary)' }}>♿ Accessibilité</p>
                    <p className="text-xs" style={{ color: 'var(--text-on-light)' }}>
                      Complexe moderne (2009) avec accès ascenseur, 4 vestiaires, et parking handicapés dédié. Conforme aux dernières normes d'accessibilité.
                    </p>
                  </div>
                )}

              </div>
            </div>

            {/* Contact Details */}
            <div className="flex items-start gap-4">
              <Phone size={24} style={{ color: 'var(--brand-primary)' }} className="flex-shrink-0 mt-1" />
              <div>
                <h3 className="heading-3 mb-2" style={{ hyphens: 'none', wordBreak: 'normal', overflowWrap: 'break-word' }}>Téléphone</h3>
                <a 
                  href={`tel:${selectedLocation.phone}`}
                  className="body-medium break-words hover:underline transition-all"
                  style={{ color: 'var(--brand-primary)' }}
                >
                  {selectedLocation.phone}
                </a>
                <p className="body-small mt-1" style={{ color: 'var(--text-secondary-light)' }}>
                  Appelez-nous pour vos questions ou nous prévenir de votre venue
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <Mail size={24} style={{ color: 'var(--brand-primary)' }} className="flex-shrink-0 mt-1" />
              <div>
                <h3 className="heading-3 mb-2" style={{ hyphens: 'none', wordBreak: 'normal', overflowWrap: 'break-word' }}>Email</h3>
                <a 
                  href={`mailto:${selectedLocation.email}`}
                  className="body-medium break-words hover:underline transition-all"
                  style={{ color: 'var(--brand-primary)' }}
                >
                  {selectedLocation.email}
                </a>
                <p className="body-small mt-1" style={{ color: 'var(--text-secondary-light)' }}>
                  Nous répondons à tous les emails au plus vite
                </p>
              </div>
            </div>

            {/* Hours for selected location */}
            <div className="flex items-start gap-4">
              <Clock size={24} style={{ color: 'var(--brand-primary)' }} className="flex-shrink-0 mt-1" />
              <div>
                <h3 className="heading-3 mb-2" style={{ hyphens: 'none', wordBreak: 'normal', overflowWrap: 'break-word' }}>Horaires</h3>
                <div className="space-y-1">
                  {Object.entries(selectedLocation.hours).map(([day, time]) => (
                    <div key={day} className="flex gap-4">
                      <span className="body-small font-medium" style={{ color: 'var(--text-on-light)', minWidth: '80px' }}>{day}:</span>
                      <span className="body-small" style={{ color: 'var(--text-on-light)' }}>{time}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* General Information & Useful Contacts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
          <div className="card-light p-6 rounded-lg">
            <div className="flex items-center gap-3 mb-4">
              <Train size={24} style={{ color: 'var(--brand-primary)' }} />
              <h3 className="heading-3" style={{ hyphens: 'none', wordBreak: 'normal', overflowWrap: 'break-word' }}>Réseau Evidence GRATUIT</h3>
            </div>
            <div className="space-y-2 body-small" style={{ color: 'var(--text-on-light)' }}>
              <p>Transport en commun entièrement gratuit avec service jusqu'à 20h15 en semaine.</p>
              <p><strong>Quatre lignes régulières + deux navettes P+R</strong></p>
              <p className="text-xs mt-3">
                <strong>Renseignements :</strong> 0806 80 10 60 - MyBus app<br/>
                <strong>Transport adapté :</strong> 0800 46 46 40
              </p>
            </div>
          </div>

          <div className="card-light p-6 rounded-lg">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-2xl">⏱️</span>
              <h3 className="heading-3" style={{ hyphens: 'none', wordBreak: 'normal', overflowWrap: 'break-word' }}>Temps de trajet</h3>
            </div>
            <div className="space-y-2 body-small" style={{ color: 'var(--text-on-light)' }}>
              <p><strong>Depuis le centre :</strong> 5 min en voiture, 10 min en bus, 15 min à pied</p>
              <p><strong>Depuis la gare :</strong> 8 min en voiture, 15 min en transport</p>
              <p><strong>Depuis Toulouse :</strong> 1h15 en voiture, 1h34 en train + transport local</p>
            </div>
          </div>
        </div>

        {/* Accessibility Information */}
        <div className="mt-6 p-6 card-light rounded-lg">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-2xl">♿</span>
            <h3 className="heading-3" style={{ hyphens: 'none', wordBreak: 'normal', overflowWrap: 'break-word' }}>Accessibilité & Transport adapté</h3>
          </div>
          <div className="body-small space-y-2" style={{ color: 'var(--text-on-light)' }}>
            <p>Tous les bus Evidence sont accessibles fauteuils roulants.</p>
            <p><strong>Transport gratuit porte-à-porte</strong> pour personnes à mobilité réduite :</p>
            <p className="ml-4">CCAS Cahors : <a href="tel:0565533040" className="hover:underline" style={{ color: 'var(--brand-primary)' }}>05 65 53 30 40</a></p>
          </div>
        </div>

        {/* Contact Form */}
        <div id="contact-form" className="mt-16">
          <h3 className="heading-2 text-center mb-8 contact-form-title" style={{ hyphens: 'none', wordBreak: 'normal', overflowWrap: 'break-word' }}>Envoyez-nous un message</h3>
          <div className="max-w-2xl mx-auto">
            <ContactForm />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Location;
